import { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  query, 
  orderBy, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebaseClient';
import { toast } from 'sonner';

export const useAssignments = (filters = {}) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let q = collection(db, 'assignments');

    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.clientId) {
      q = query(q, where('user_id', '==', filters.clientId));
    }

    // Order by created date (newest first)
    q = query(q, orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const assignmentsData = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            created_at: doc.data().created_at?.toDate?.() || new Date(),
            deadline: doc.data().deadline?.toDate?.() || new Date(),
          }))
          .filter((assignment) => !assignment.deleted && assignment.id); // Filter out deleted or invalid assignments
        
        setAssignments(assignmentsData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error fetching assignments:', error);
        setError(error);
        setLoading(false);
        toast.error('Failed to load assignments');
      }
    );

    return () => unsubscribe();
  }, [filters.status, filters.clientId]);

  const createAssignment = async (assignmentData) => {
    try {
      const docRef = await addDoc(collection(db, 'assignments'), {
        ...assignmentData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: 'pending',
      });
      toast.success('Assignment created successfully');
      return docRef.id;
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
      throw error;
    }
  };

  const updateAssignment = async (id, updates) => {
    try {
      const assignmentRef = doc(db, 'assignments', id);
      await updateDoc(assignmentRef, {
        ...updates,
        updated_at: serverTimestamp(),
      });
      toast.success('Assignment updated successfully');
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error('Failed to update assignment');
      throw error;
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const assignmentRef = doc(db, 'assignments', id);
      await updateDoc(assignmentRef, {
        status,
        updated_at: serverTimestamp(),
      });
      // Don't show toast here as ActionButtons handles it
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  };

  const deleteAssignment = async (id) => {
    try {
      const assignmentRef = doc(db, 'assignments', id);
      await deleteDoc(assignmentRef);
      // Don't show toast here as ActionButtons handles it
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error;
    }
  };

  const getAssignmentsByStatus = (status) => {
    return assignments.filter(assignment => assignment.status === status);
  };

  const getAssignmentStats = () => {
    const stats = assignments.reduce((acc, assignment) => {
      acc[assignment.status] = (acc[assignment.status] || 0) + 1;
      return acc;
    }, {});

    return {
      total: assignments.length,
      pending: stats.pending || 0,
      'in-progress': stats['in-progress'] || 0,
      completed: stats.completed || 0,
      cancelled: stats.cancelled || 0,
    };
  };

  return {
    assignments,
    loading,
    error,
    createAssignment,
    updateAssignment,
    updateStatus,
    deleteAssignment,
    getAssignmentsByStatus,
    getAssignmentStats,
  };
};